
import React, { useState, useEffect } from 'react';
import saleService, { Sale, PaymentMethod } from '../services/saleService';
import { formatCurrency } from '../utils/format';
import toast from 'react-hot-toast';
import { Trash2, Plus, Edit, CheckCircle, History as HistoryIcon, Calendar, User as UserIcon } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import { CentralEditModal } from './products/CentralEditModal';
import { EditCustomerModal } from './customers/EditCustomerModal';
import customerService, { Customer } from '../services/customerService';

interface SaleDetailModalProps {
    sale: Sale;
    onClose: () => void;
    onSaleUpdated?: () => void;
}

export const SaleDetailModal: React.FC<SaleDetailModalProps> = ({ sale, onClose, onSaleUpdated }) => {
    const { user } = useAuth();
    const [fullSale, setFullSale] = useState<Sale | null>(null);
    const [loading, setLoading] = useState(true);
    const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
    const [completionPayments, setCompletionPayments] = useState<{ method: PaymentMethod; amount: number }[]>([]);

    // Central Edit Modal State
    const [showCentralEditModal, setShowCentralEditModal] = useState(false);
    const [itemToEdit, setItemToEdit] = useState<any>(null);

    // Customer edit state
    const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
    const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);

    // Cancel Sale State
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');

    // Cancel Item State
    const [itemToCancel, setItemToCancel] = useState<any>(null);
    const [cancelItemReason, setCancelItemReason] = useState('');

    // Calculate remaining amount whenever fullSale changes
    useEffect(() => {
        if (fullSale) {
            const remaining = fullSale.total - (fullSale.amountPaid || 0);
            if (remaining > 0) {
                setCompletionPayments([{ method: 'CASH', amount: remaining }]);
            }
        }
    }, [fullSale]);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const response = await saleService.getSaleById(sale.id);
                setFullSale(response.sale);
            } catch (error) {
                toast.error('Error al cargar detalles');
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [sale]);

    const handleCompleteSale = async () => {
        if (!fullSale) return;

        try {
            setLoading(true);
            setShowPaymentMethodModal(false);

            const targetSaleId = fullSale.saleContext?.rootSaleId || fullSale.id;

            await saleService.createPaymentNote(targetSaleId, {
                payments: completionPayments,
                notes: `Liquidación de venta original ${fullSale.folio}`
            });
            toast.success('Nota de liquidación generada exitosamente');

            // Refresh details
            const response = await saleService.getSaleById(fullSale.id);
            setFullSale(response.sale);

            if (onSaleUpdated) {
                onSaleUpdated();
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al completar venta');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPaymentMethodModal = () => {
        setShowPaymentMethodModal(true);
    };

    const handleOpenCentralEdit = (item: any) => {
        if (item.sales?.status === 'CANCELADA' || fullSale?.status === 'CANCELADA') {
            toast.error('No se pueden modificar items de ventas canceladas');
            return;
        }

        // Ensure the item.product has the necessary nested values so CentralEditModal can read it
        const completeItem = {
            ...item,
            product: item.product ? {
                ...item.product,
                id_producto: item.productId,
                inventoryType: item.inventoryType,
                cod_producto: item.productCode || item.product?.cod_producto,
            } : null
        };

        setItemToEdit(completeItem);
        setShowCentralEditModal(true);
    };

    // Abonos (Progressive Payments) State
    const [showAbonoModal, setShowAbonoModal] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
    const [editingPaymentSaleId, setEditingPaymentSaleId] = useState<number | null>(null);
    const [abonoData, setAbonoData] = useState({
        amount: 0,
        method: 'CASH' as PaymentMethod,
        reference: '',
        notas: ''
    });

    const handleAbonoSubmit = async () => {
        if (!fullSale) return;
        if (abonoData.amount <= 0) {
            toast.error('El monto debe ser mayor a 0');
            return;
        }

        try {
            setLoading(true);
            const targetSaleId = editingPaymentSaleId || fullSale.saleContext?.rootSaleId || fullSale.id;

            if (editingPaymentId) {
                await saleService.editPayment(targetSaleId, editingPaymentId, abonoData);
                toast.success('Abono actualizado exitosamente');
            } else {
                // Generar nota de venta individual para el abono
                await saleService.createPaymentNote(targetSaleId, {
                    amount: abonoData.amount,
                    method: abonoData.method,
                    notes: abonoData.notas
                });
                toast.success('Nota de abono generada exitosamente');
            }

            setShowAbonoModal(false);
            setEditingPaymentId(null);
            setEditingPaymentSaleId(null);

            // Refresh details
            const response = await saleService.getSaleById(fullSale.id);
            setFullSale(response.sale);
            if (onSaleUpdated) onSaleUpdated();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al guardar abono');
        } finally {
            setLoading(false);
        }
    };

    const handleEditPaymentClick = (payment: any) => {
        setEditingPaymentId(payment.id);
        setEditingPaymentSaleId(payment.saleId);
        setAbonoData({
            amount: Number(payment.amount),
            method: payment.method as PaymentMethod,
            reference: payment.reference || '',
            notas: payment.notas || ''
        });
        setShowAbonoModal(true);
    };

    const handleDeletePayment = async (paymentId: number, saleId: number) => {
        if (!fullSale) return;
        if (window.confirm('¿Estás seguro de cancelar este abono? El saldo de la venta regresará a su estado anterior.')) {
            try {
                setLoading(true);
                await saleService.deletePayment(saleId, paymentId);
                toast.success('Abono cancelado exitosamente');

                // Refresh details
                const response = await saleService.getSaleById(fullSale.id);
                setFullSale(response.sale);
                if (onSaleUpdated) onSaleUpdated();
            } catch (error: any) {
                console.error(error);
                toast.error(error.response?.data?.message || 'Error al cancelar abono');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCancelSale = async () => {
        if (!fullSale) return;
        if (!cancelReason.trim()) {
            toast.error('Debe ingresar un motivo de cancelación');
            return;
        }

        try {
            setLoading(true);
            await saleService.cancelSale(fullSale.id, cancelReason);
            toast.success('Venta cancelada exitosamente');
            setShowCancelModal(false);
            
            // Si la venta se cancela, ya no debería aparecer en la lista (según backend logic)
            // Cerramos el modal y notificamos al padre para refrescar
            onClose();
            if (onSaleUpdated) onSaleUpdated();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al cancelar la venta');
        } finally {
            setLoading(false);
        }
    };


    const handleCancelItem = async () => {
        if (!fullSale || !itemToCancel) return;
        if (cancelItemReason.trim().length < 5) {
            toast.error('Debe ingresar un motivo de cancelación');
            return;
        }

        try {
            setLoading(true);
            // Los artículos siempre pertenecen a la venta original (raíz)
            const rootSaleId = itemToCancel.saleId || fullSale.saleContext?.rootSaleId || fullSale.id;
            const result = await saleService.cancelSaleItem(rootSaleId, itemToCancel.id, cancelItemReason);

            const inventoryMsg = result?.inventoryAction === 'DELETED_PRE_ORDER'
                ? 'El pre-order fue eliminado.'
                : result?.inventoryAction === 'RETURNED_TO_STOCK'
                    ? 'El artículo regresó al inventario.'
                    : '';
            toast.success(`Artículo cancelado. Nuevo total: ${formatCurrency(result?.newTotal || 0)}. ${inventoryMsg}`, { duration: 6000 });

            setItemToCancel(null);
            setCancelItemReason('');

            const response = await saleService.getSaleById(fullSale.id);
            setFullSale(response.sale);
            if (onSaleUpdated) onSaleUpdated();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al cancelar el artículo', { duration: 8000 });
        } finally {
            setLoading(false);
        }
    };

    if (!fullSale && loading) return <div className="fixed inset-0 bg-transparent flex items-center justify-center z-[60]">Cargando...</div>;
    if (!fullSale) return null;

    const isAdmin = user?.rol_usr === UserRole.ADMIN;
    
    // El timeline ahora viene directamente del backend enriquecido
    const fullPaymentHistory = fullSale.paymentTimeline || [];
    const context = fullSale.saleContext;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-white rounded-t-xl relative overflow-hidden">
                    {/* Indicador visual de tipo de nota */}
                    <div className={`absolute top-0 left-0 w-1.5 h-full ${fullSale.parentSaleId ? 'bg-blue-500' : 'bg-purple-600'}`} />
                    
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                                {fullSale.folio || `#${fullSale.id}`}
                            </h2>
                            <div className="flex items-center gap-2">
                                {fullSale.parentSaleId ? (
                                    <span className="bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full font-black uppercase tracking-widest border border-blue-200 shadow-sm">
                                        NOTA DE PAGO
                                    </span>
                                ) : (
                                    <span className="bg-purple-100 text-purple-700 text-xs px-2.5 py-1 rounded-full font-black uppercase tracking-widest border border-purple-200 shadow-sm">
                                        VENTA ORIGINAL
                                    </span>
                                )}
                                <span className={`text-xs px-2.5 py-1 rounded-full font-black uppercase tracking-widest border shadow-sm ${
                                    fullSale.status === 'COMPLETADA' ? 'bg-green-100 text-green-700 border-green-200' :
                                    fullSale.status === 'CANCELADA' ? 'bg-red-100 text-red-700 border-red-200' :
                                    'bg-amber-100 text-amber-700 border-amber-200'
                                }`}>
                                    {fullSale.status}
                                </span>
                            </div>
                        </div>
                        {context && context.isNote && (
                            <p className="text-sm font-bold text-blue-600 mt-1.5 flex items-center gap-1.5">
                                <HistoryIcon size={16} />
                                Vinculado a Venta: <span className="underline decoration-2 underline-offset-2">{context.rootFolio}</span>
                            </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1 font-medium flex items-center gap-1">
                            <Calendar size={14} className="text-gray-300" />
                            {new Date(fullSale.createdAt).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}
                        </p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all text-xl">✕</button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-blue-50 p-4 rounded-lg relative group">
                            <div className="flex justify-between items-center border-b border-blue-200 pb-2 mb-2">
                                <h3 className="font-bold text-gray-900">Cliente</h3>
                                {fullSale.customerRecordId && (
                                    <button 
                                        onClick={async () => {
                                            try {
                                                const customerData = await customerService.getCustomerById(fullSale.customerRecordId!);
                                                setCustomerToEdit(customerData);
                                                setShowEditCustomerModal(true);
                                            } catch (e) {
                                                toast.error('Error al cargar datos del cliente');
                                            }
                                        }}
                                        className="text-blue-600 hover:bg-blue-100 p-1.5 rounded transition-colors"
                                        title="Editar Cliente"
                                    >
                                        <Edit size={16} />
                                    </button>
                                )}
                            </div>
                            <p><strong>Nombre:</strong> {fullSale.customerName}</p>
                            <p><strong>Teléfono:</strong> {fullSale.customerPhone || 'N/A'}</p>
                            <p><strong>Email:</strong> {fullSale.customerEmail || 'N/A'}</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gray-900 border-b border-purple-200 pb-2 mb-2">Venta</h3>
                            <p className="text-sm"><strong>Vendedora:</strong> {fullSale.seller?.username || 'N/A'}</p>
                            <div className="mb-2">
                                <div className="flex items-center justify-between border-b border-purple-200 pb-2 mt-4 mb-3">
                                    <strong className="text-gray-900 flex items-center gap-2 text-xs uppercase tracking-wider">
                                        <HistoryIcon size={16} className="text-purple-600" />
                                        Historial de Pagos
                                    </strong>
                                    {context && context.balance > 0.01 && (fullSale.status !== 'CANCELADA') && (
                                        <button
                                            onClick={() => {
                                                setAbonoData({
                                                    amount: 0,
                                                    method: 'CASH',
                                                    reference: '',
                                                    notas: ''
                                                });
                                                setEditingPaymentId(null);
                                                setShowAbonoModal(true);
                                            }}
                                            className="text-[11px] bg-purple-600 text-white px-2.5 py-1.5 rounded hover:bg-purple-700 font-bold flex items-center gap-1 shadow-sm transition-all uppercase tracking-wider"
                                        >
                                            <Plus size={12} /> Abono Rápido
                                        </button>
                                    )}
                                </div>

                                {fullPaymentHistory.length > 0 ? (
                                    <div className="space-y-3 max-h-72 overflow-y-auto pr-2 custom-scrollbar">
                                        {fullPaymentHistory.map((p: any) => {
                                            const isCurrentNotePayment = fullSale.payments?.some((sp: any) => sp.id === p.id);
                                            const isCancelled = p.status === 'CANCELADO' || p.status === 'CANCELADA';
                                            
                                            return (
                                                <div 
                                                    key={p.id} 
                                                    className={`relative pl-4 border-l-2 ${
                                                        isCancelled ? 'border-red-200 bg-red-50/30' : 
                                                        isCurrentNotePayment ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-gray-50/30'
                                                    } p-3 rounded-r-lg transition-colors`}
                                                >
                                                    {/* Punto de la línea de tiempo */}
                                                    <div className={`absolute -left-[5px] top-4 w-2 h-2 rounded-full ring-2 ring-white ${
                                                        isCancelled ? 'bg-red-400' : 
                                                        isCurrentNotePayment ? 'bg-blue-600' : 'bg-gray-400'
                                                    }`} />

                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1.5">
                                                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded shadow-sm ${
                                                                    p.saleType === 'LIQUIDACION' ? 'bg-green-100 text-green-700' :
                                                                    p.saleType === 'ABONO' ? 'bg-blue-100 text-blue-700' :
                                                                    p.isRoot ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                                                                }`}>
                                                                    {p.isRoot ? 'ANTICIPO' : (p.saleType || 'PAGO')}
                                                                </span>
                                                                <span className="text-xs font-mono font-bold text-gray-400">{p.saleFolio}</span>
                                                                {isCurrentNotePayment && (
                                                                    <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-md font-black uppercase tracking-widest animate-pulse">Esta nota</span>
                                                                )}
                                                            </div>
                                                            
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-bold text-gray-800">
                                                                    {p.method === 'CASH' ? 'Efectivo' : p.method === 'CARD' ? 'Tarjeta' : p.method === 'TRANSFER' ? 'Transf.' : p.method}
                                                                </span>
                                                                <span className={`text-base font-black ${isCancelled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{formatCurrency(p.amount)}</span>
                                                            </div>
                                                            
                                                            <div className="text-xs text-gray-500 mt-1.5 flex flex-wrap items-center gap-x-2">
                                                                <span className="flex items-center gap-1 font-medium"><Calendar size={12} className="text-gray-300" /> {new Date(p.createdAt).toLocaleDateString()}</span>
                                                                <span className="flex items-center gap-1 font-medium text-gray-400">
                                                                    <UserIcon size={12} className="text-gray-300" />
                                                                    {p.user?.username || 'Sistema'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {isAdmin && !isCancelled && (
                                                            <div className="flex gap-1 ml-2">
                                                                <button onClick={() => handleEditPaymentClick(p)} className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors" title="Editar">
                                                                    <Edit size={12} />
                                                                </button>
                                                                <button onClick={() => handleDeletePayment(p.id, p.saleId)} className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors" title="Cancelar">
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {p.notas && <div className="mt-1.5 text-[9px] text-gray-500 italic border-t border-gray-100 pt-1">"{p.notas}"</div>}
                                                    {isCancelled && <div className="mt-1 text-[8px] text-red-600 font-black uppercase text-right tracking-widest">Pago Cancelado</div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                                        <HistoryIcon size={24} className="mx-auto text-gray-200 mb-2" />
                                        <p className="text-xs text-gray-400 italic font-medium tracking-tight">Sin movimientos en el proyecto</p>
                                    </div>
                                )}
                            </div>
                            <p className="flex items-center gap-2">
                                <strong>Estado:</strong>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${fullSale.status === 'COMPLETADA' || fullSale.status === 'COMPLETED'
                                    ? 'bg-green-100 text-green-700'
                                    : fullSale.status === 'PRE-ORDER'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : fullSale.status === 'APARTADO'
                                            ? 'bg-purple-100 text-purple-700'
                                            : 'bg-gray-100 text-gray-700'
                                    }`}>
                                    {fullSale.status === 'PRE-ORDER' ? 'PRE-ORDER' : fullSale.status}
                                </span>
                                {fullSale.status === 'APARTADO' && (fullSale as any).isPreOrder && (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-200">
                                        Origen: PRE-ORDER
                                    </span>
                                )}
                            </p>
                            {/* @ts-ignore */}
                            {fullSale.discountReason && <p><strong>Descuento:</strong> {fullSale.discountReason}</p>}

                            {(fullSale.status === 'APARTADO' || fullSale.status === 'PRE-ORDER' || fullSale.status === 'PENDIENTE') && (fullSale.total - (fullSale.amountPaid || 0) > 0.01) && (
                                <button
                                    onClick={handleOpenPaymentMethodModal}
                                    className="mt-3 w-full py-2 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition"
                                >
                                    Completar Venta
                                </button>
                            )}

                            {isAdmin && fullSale.status !== 'CANCELADA' && (
                                <button
                                    onClick={() => setShowCancelModal(true)}
                                    className="mt-2 w-full py-2 bg-white text-red-600 border border-red-200 rounded-lg font-semibold hover:bg-red-50 transition flex items-center justify-center gap-2"
                                >
                                    <Trash2 size={16} /> Cancelar Venta
                                </button>
                            )}
                        </div>
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
                            {/* Marca de agua */}
                            <div className="absolute -top-4 -right-4 opacity-5 transform rotate-12">
                                <HistoryIcon size={100} />
                            </div>

                            {context ? (
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                                        <h3 className="font-black text-xs uppercase tracking-widest text-gray-400">Resumen de Saldos</h3>
                                        <span className="text-xs font-mono font-bold text-gray-300">REF: {context.rootFolio}</span>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500 font-medium">Costo Total Venta:</span>
                                            <span className="font-bold text-gray-900">{formatCurrency(context.totalGlobal)}</span>
                                        </div>

                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500 font-medium">Abonado Acumulado:</span>
                                            <span className="font-black text-blue-600">{formatCurrency(context.totalPaidGlobal)}</span>
                                        </div>

                                        {context.isNote && (
                                            <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 flex justify-between items-center mt-3 group">
                                                <span className="text-xs font-bold text-blue-700 uppercase tracking-tight">Monto de esta nota:</span>
                                                <span className="text-base font-black text-blue-800 bg-white px-2.5 py-1 rounded shadow-sm border border-blue-100">
                                                    {formatCurrency(fullSale.total)}
                                                </span>
                                            </div>
                                        )}

                                        <div className="pt-5 mt-4 border-t border-gray-100">
                                            <div className="flex justify-between items-end">
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-black uppercase tracking-widest text-red-500 leading-none mb-2">Saldo Pendiente</span>
                                                    <span className="text-3xl font-black text-red-600 tracking-tighter leading-none">
                                                        {formatCurrency(context.balance)}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    {context.balance <= 0.01 ? (
                                                        <span className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-md font-black uppercase tracking-widest animate-bounce">LIQUIDADO</span>
                                                    ) : (
                                                        <span className="bg-red-100 text-red-600 text-[11px] px-3 py-1.5 rounded-md font-black uppercase tracking-widest border border-red-200">SIN LIQUIDAR</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="animate-pulse flex flex-col space-y-3">
                                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                                    <div className="h-10 bg-gray-100 rounded w-full"></div>
                                    <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        Productos 
                        {fullSale.parentSaleId && !fullSale.items?.length && (
                            <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                (Mostrando productos de la venta original)
                            </span>
                        )}
                    </h3>
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b-2 border-gray-200 text-gray-600 text-sm">
                                <th className="py-2">Código</th>
                                <th className="py-2">Producto</th>
                                <th className="py-2">Color/Talla</th>
                                <th className="py-2 text-center">Tipo</th>
                                <th className="py-2 text-center">Cant.</th>
                                <th className="py-2 text-right">Precio Unit.</th>
                                <th className="py-2 text-right">Total</th>
                                {isAdmin && <th className="py-2 text-center">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {(fullSale.items?.length > 0 ? fullSale.items : (fullSale.parentSale?.items || [])).map((item: any, idx: number) => {
                                const isDelivered = item.product?.invoice_id && !item.product?.in_transit;
                                return (
                                    <tr key={idx} className={`border-b border-gray-100 hover:bg-gray-50 ${isDelivered ? 'opacity-60 bg-gray-50' : ''}`}>
                                        <td className="py-3 text-sm text-gray-500 font-mono">
                                            {item.productCode || '-'}
                                            {isDelivered && <div className="text-[10px] text-green-600 font-bold uppercase tracking-wider">RECIBIDO</div>}
                                        </td>
                                        <td className="py-3 font-medium text-gray-900">
                                            <span className={isDelivered ? 'line-through decoration-emerald-500/50 decoration-2' : ''}>
                                                {item.productName}
                                            </span>
                                        </td>
                                        <td className="py-3 text-sm text-gray-600">
                                            {item.product?.color && <span className="mr-2">{item.product.color}</span>}
                                            {item.product?.talla && <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-bold border border-gray-200">{item.product.talla}</span>}
                                        </td>
                                        <td className="py-3 text-center">
                                            {item.isPreOrder ? (
                                                <div className="flex flex-col items-center gap-1">
                                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-bold">PRE-ORDER</span>
                                                </div>
                                            ) : (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-bold">STOCK</span>
                                            )}
                                        </td>
                                        <td className="py-3 text-center font-bold">{item.quantity}</td>
                                        <td className="py-3 text-right">{formatCurrency(item.unitPrice)}</td>
                                        <td className="py-3 text-right font-bold">{formatCurrency(item.total)}</td>
                                        {isAdmin && (
                                            <td className="py-3 text-center">
                                                {(fullSale.status === 'APARTADO' || fullSale.status === 'PRE-ORDER' || fullSale.status === 'PENDIENTE' || fullSale.status === 'COMPLETADA') && (
                                                    <div className="flex flex-col gap-1 items-center">
                                                        <button
                                                            onClick={() => handleOpenCentralEdit(item)}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                                                            title="Modificar artículo"
                                                        >
                                                            <Edit size={14} /> Editar
                                                        </button>
                                                        {(fullSale.items?.length > 0 ? fullSale.items : (fullSale.parentSale?.items || [])).length > 1 && (
                                                            <button
                                                                onClick={() => { setItemToCancel(item); setCancelItemReason(''); }}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                                                                title="Cancelar este artículo de la venta"
                                                            >
                                                                <Trash2 size={14} /> Cancelar
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {isDelivered && (
                                                    <div className="flex flex-col gap-1 items-center justify-center">
                                                        <span className="text-green-600 text-xs font-bold flex items-center gap-1 mb-1">
                                                            <CheckCircle size={14} /> Entregado
                                                        </span>
                                                    </div>
                                                )}
                                                {!isDelivered && fullSale.status === 'COMPLETADA' && !fullSale.parentSaleId && (
                                                     <span className="text-gray-400 text-xs flex items-center justify-center gap-1" title="Venta de stock entregada al momento">
                                                       <CheckCircle size={14} /> (Stock) Entregado
                                                     </span>
                                                 )}
                                                 {fullSale.parentSaleId && (
                                                     <span className="text-[10px] text-blue-400 italic">Ver venta original</span>
                                                 )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de selección de método de pago */}
            {showPaymentMethodModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[110]" onClick={() => setShowPaymentMethodModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Completar Pago</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            Monto Restante: <strong>{formatCurrency((fullSale?.total || 0) - (fullSale?.amountPaid || 0))}</strong>
                        </p>

                        <div className="space-y-3 mb-6">
                            {completionPayments.map((p, idx) => (
                                <div key={idx} className="flex gap-2">
                                    <select
                                        value={p.method}
                                        onChange={(e) => {
                                            const newPayments = [...completionPayments];
                                            newPayments[idx].method = e.target.value as PaymentMethod;
                                            setCompletionPayments(newPayments);
                                        }}
                                        className="w-1/3 px-2 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                                    >
                                        <option value="CASH">Efectivo</option>
                                        <option value="CARD">Tarjeta</option>
                                        <option value="TRANSFER">Transf.</option>
                                        {(user?.rol_usr === UserRole.ADMIN || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                                            <>
                                                <option value="MERCADO_PAGO">Mercado Pago</option>
                                                <option value="KUESKI">Kueski Pay</option>
                                            </>
                                        )}
                                    </select>
                                    <input
                                        type="number"
                                        value={p.amount}
                                        onChange={(e) => {
                                            const newPayments = [...completionPayments];
                                            newPayments[idx].amount = parseFloat(e.target.value) || 0;
                                            setCompletionPayments(newPayments);
                                        }}
                                        className="flex-1 px-2 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                                        placeholder="Monto"
                                    />
                                    <button
                                        onClick={() => setCompletionPayments(completionPayments.filter((_, i) => i !== idx))}
                                        className="text-red-500 hover:text-red-700 p-2"
                                        disabled={completionPayments.length === 1}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}

                            <button
                                onClick={() => setCompletionPayments([...completionPayments, { method: 'CASH', amount: 0 }])}
                                className="text-sm text-blue-600 flex items-center gap-1 font-medium hover:text-blue-800"
                            >
                                <Plus size={16} /> Agregar Método
                            </button>

                            <div className="flex justify-between text-sm mt-4 pt-2 border-t border-gray-100 font-bold">
                                <span>Total a Pagar:</span>
                                <span className={Math.abs(completionPayments.reduce((s, p) => s + p.amount, 0) - ((fullSale?.total || 0) - (fullSale?.amountPaid || 0))) < 0.1 ? 'text-green-600' : 'text-orange-600'}>
                                    {formatCurrency(completionPayments.reduce((s, p) => s + p.amount, 0))}
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowPaymentMethodModal(false)}
                                className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleCompleteSale}
                                disabled={loading}
                                className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition disabled:opacity-50"
                            >
                                {loading ? 'Procesando...' : 'Completar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCentralEditModal && itemToEdit && (
                <CentralEditModal
                    isOpen={showCentralEditModal}
                    onClose={() => setShowCentralEditModal(false)}
                    saleId={itemToEdit.saleId || fullSale.id}
                    saleItem={itemToEdit}
                    onSuccess={async () => {
                        const response = await saleService.getSaleById(fullSale.id);
                        setFullSale(response.sale);
                        if (onSaleUpdated) onSaleUpdated();
                    }}
                />
            )}
            {/* Modal de Abono (Agregar / Editar) */}
            {showAbonoModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[120]" onClick={() => setShowAbonoModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">{editingPaymentId ? 'Editar Abono' : 'Agregar Abono'}</h3>

                        {!editingPaymentId && (
                            <p className="text-sm text-gray-600 mb-6">
                                Saldo Pendiente: <strong>{formatCurrency((fullSale?.total || 0) - (fullSale?.amountPaid || 0))}</strong>
                            </p>
                        )}

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
                                <input
                                    type="number"
                                    value={abonoData.amount}
                                    onChange={(e) => setAbonoData({ ...abonoData, amount: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-lg font-bold"
                                    placeholder="0.00"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago *</label>
                                <select
                                    value={abonoData.method}
                                    onChange={(e) => setAbonoData({ ...abonoData, method: e.target.value as PaymentMethod })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="CASH">Efectivo</option>
                                    <option value="CARD">Tarjeta</option>
                                    <option value="TRANSFER">Transferencia</option>
                                    {(user?.rol_usr === UserRole.ADMIN || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                                        <>
                                            <option value="MERCADO_PAGO">Mercado Pago</option>
                                            <option value="KUESKI">Kueski Pay</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Referencia (Opcional)</label>
                                <input
                                    type="text"
                                    value={abonoData.reference}
                                    onChange={(e) => setAbonoData({ ...abonoData, reference: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    placeholder="Folio de transferencia, terminación de tarjeta..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notas (Opcional)</label>
                                <textarea
                                    value={abonoData.notas}
                                    onChange={(e) => setAbonoData({ ...abonoData, notas: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none h-20"
                                    placeholder="Detalles sobre este cobro..."
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowAbonoModal(false)}
                                className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAbonoSubmit}
                                disabled={loading || abonoData.amount <= 0}
                                className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? 'Procesando...' : editingPaymentId ? 'Guardar Cambios' : 'Registrar Abono'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Cancelación de Venta */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[130]" onClick={() => setShowCancelModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border-t-8 border-red-600 overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
                        
                        <div className="flex items-center gap-3 mb-4 text-red-600">
                            <div className="p-3 bg-red-100 rounded-full">
                                <Trash2 size={28} className="text-red-700" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black uppercase tracking-tight">Cancelar Venta</h3>
                                <p className="text-xs font-bold text-red-500 uppercase tracking-widest mt-0.5">Acción Irreversible</p>
                            </div>
                        </div>
                        
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 text-sm text-red-800 space-y-3">
                            <p>
                                ¿Estás seguro de cancelar la nota <strong>{fullSale.folio || fullSale.id}</strong>?
                            </p>
                            <ul className="list-disc pl-5 space-y-1 text-xs font-medium">
                                <li><strong>Finanzas:</strong> Se anulará la nota principal y <strong>TODOS sus abonos o liquidaciones</strong> vinculadas (quedarán en $0.00).</li>
                                <li><strong>Inventario:</strong> Los productos de <i>Stock</i> regresarán al inventario.</li>
                                <li><strong>Pre-orders:</strong> Los productos bajo pedido serán <strong>eliminados permanentemente</strong>.</li>
                                <li><strong>Comisiones:</strong> Toda comisión vinculada pasará a estado Cancelada.</li>
                            </ul>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Motivo de Cancelación (Requerido) *</label>
                            <textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none h-24"
                                placeholder="Especifique detalladamente por qué se está cancelando todo este folio..."
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition"
                            >
                                Volver sin Hacer Nada
                            </button>
                            <button
                                onClick={handleCancelSale}
                                disabled={loading || cancelReason.trim().length < 5}
                                className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? 'Procesando...' : 'Confirmar Cancelación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal de Cancelación de Artículo */}
            {itemToCancel && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[130]" onClick={() => setItemToCancel(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border-t-8 border-red-600 overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4 text-red-600">
                            <div className="p-3 bg-red-100 rounded-full">
                                <Trash2 size={28} className="text-red-700" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black uppercase tracking-tight">Cancelar Artículo</h3>
                                <p className="text-xs font-bold text-red-500 uppercase tracking-widest mt-0.5">Acción Irreversible</p>
                            </div>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 text-sm text-red-800 space-y-3">
                            <p>
                                ¿Cancelar <strong>{itemToCancel.productName}</strong> ({itemToCancel.productCode || 'sin código'}) por <strong>{formatCurrency(itemToCancel.total)}</strong>?
                            </p>
                            <ul className="list-disc pl-5 space-y-1 text-xs font-medium">
                                <li><strong>Venta:</strong> El total de la nota se recalcula sin este artículo.</li>
                                {itemToCancel.isPreOrder ? (
                                    <li><strong>Pre-order:</strong> El producto creado para esta venta será <strong>eliminado permanentemente</strong>.</li>
                                ) : (
                                    <li><strong>Inventario:</strong> El artículo <strong>regresa al inventario</strong>.</li>
                                )}
                                <li><strong>Pagos:</strong> Si lo ya pagado supera el nuevo total, primero hay que corregir o eliminar un pago.</li>
                            </ul>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Motivo (Requerido) *</label>
                            <textarea
                                value={cancelItemReason}
                                onChange={(e) => setCancelItemReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none h-24"
                                placeholder="¿Por qué se cancela este artículo?"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setItemToCancel(null)}
                                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition"
                            >
                                Volver
                            </button>
                            <button
                                onClick={handleCancelItem}
                                disabled={loading || cancelItemReason.trim().length < 5}
                                className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition disabled:opacity-50"
                            >
                                {loading ? 'Procesando...' : 'Cancelar Artículo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal de Edición de Cliente */}
            {showEditCustomerModal && customerToEdit && (
                <EditCustomerModal
                    customer={customerToEdit}
                    onClose={() => {
                        setShowEditCustomerModal(false);
                        setCustomerToEdit(null);
                    }}
                    onSave={async (id, data) => {
                        try {
                            if (id !== 0) {
                                await customerService.updateCustomer(id, data);
                                toast.success('Cliente actualizado correctamente');
                                // Refresh the sale detail so it shows updated snapshots
                                const response = await saleService.getSaleById(fullSale.id);
                                setFullSale(response.sale);
                                if (onSaleUpdated) onSaleUpdated();
                            }
                            setShowEditCustomerModal(false);
                            setCustomerToEdit(null);
                        } catch (e: any) {
                            toast.error(e.response?.data?.message || 'Error al guardar cliente');
                        }
                    }}
                />
            )}
        </div>
    );
};
